import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { comparePriceWith, formatAgentModelPrice, getAgentModelName } from '../lib/agentModels';
import type { AgentModelOption, AgentModelOptionsResponse } from '../services/projects.api';

const TAG_CLASS = 'rounded-full px-1.5 py-0 text-[10px] font-medium';

/** Production, recommended and price-versus-production tags. Text and color only. */
export const ModelTags = ({
  option,
  currentModel,
  currentOption,
}: {
  option: AgentModelOption;
  currentModel: string;
  currentOption: AgentModelOption | undefined;
}): JSX.Element => {
  const priceComparison = comparePriceWith(option, currentOption);
  return (
    <>
      {option.id === currentModel && (
        <Badge className={`${TAG_CLASS} bg-gray-200 text-gray-700 hover:bg-gray-200`}>En producción</Badge>
      )}
      {option.isNewProjectDefault && (
        <Badge className={`${TAG_CLASS} bg-indigo-50 text-indigo-600 hover:bg-indigo-50`}>Recomendado</Badge>
      )}
      {priceComparison === 'cheaper' && (
        <Badge className={`${TAG_CLASS} bg-emerald-100 text-emerald-700 hover:bg-emerald-100`}>Más barato</Badge>
      )}
      {priceComparison === 'more-expensive' && (
        <Badge className={`${TAG_CLASS} bg-rose-100 text-rose-700 hover:bg-rose-100`}>Más caro</Badge>
      )}
    </>
  );
};

export interface AgentModelFieldProps {
  id: string;
  value: string;
  onChange: (modelId: string) => void;
  modelOptions: AgentModelOptionsResponse;
  /** Shows that real clients keep the production model, for a model that only runs in Demo Chat. */
  showDemoChatNote: boolean;
}

/** Model select with prices and tags, and a summary of the selected model. */
const AgentModelField = ({
  id,
  value,
  onChange,
  modelOptions,
  showDemoChatNote,
}: AgentModelFieldProps): JSX.Element => {
  const { options, currentModel } = modelOptions;
  const selectedOption = options.find((option) => option.id === value);
  const currentOption = options.find((option) => option.id === currentModel);
  const selectedPrice = selectedOption && formatAgentModelPrice(selectedOption);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>Selección de modelo</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="h-9 w-full">
          <SelectValue>{getAgentModelName(value, options)}</SelectValue>
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          {options.map((option) => {
            const price = formatAgentModelPrice(option);
            return (
              <SelectItem key={option.id} value={option.id} className="py-1.5">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="flex items-center gap-1.5">
                    {getAgentModelName(option.id, options)}
                    <ModelTags option={option} currentModel={currentModel} currentOption={currentOption} />
                  </span>
                  {price && <span className="text-[11px] text-text-muted">{price}</span>}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <div className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle bg-gray-50 text-xs">
        {selectedOption && (
          <div className="flex flex-col gap-1 px-3 py-2">
            <span className="text-text-default">{selectedOption.description}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-text-muted">
              {selectedPrice && `${selectedPrice} por 1M tokens`}
              <ModelTags option={selectedOption} currentModel={currentModel} currentOption={currentOption} />
            </span>
          </div>
        )}
        {showDemoChatNote && value !== currentModel && (
          <div className="flex gap-2 px-3 py-2">
            <span className="shrink-0 font-medium text-text-default">Clientes reales</span>
            <span className="text-text-muted">
              Siguen con <strong className="text-text-default">{getAgentModelName(currentModel, options)}</strong>. Este
              modelo solo responde en Demo Chat.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentModelField;
